/**
 * DisplayObjectRegistry - Factory registry for DisplayObject creation.
 *
 * Maps (FeatureType, ViewMode) pairs to factory functions.
 * Viewports use this registry to create appropriate DisplayObjects for features.
 *
 * Pattern: Similar to WorkflowRegistry but as a service for lifecycle management.
 *
 * @example
 * ```typescript
 * const registry = serviceManager.getService<DisplayObjectRegistry>(
 *     DisplayObjectRegistry.SERVICE_NAME
 * );
 *
 * // Register a factory (typically done at app initialization)
 * registry.register(
 *     DisplayFeatureType.CENTERLINE,
 *     ViewMode.VIEW_3D,
 *     (feature, mode) => new CenterlinesDisplayObject3D(feature, mode)
 * );
 *
 * // Create a DisplayObject (typically done by viewports)
 * const displayObject = registry.create(feature, ViewMode.VIEW_3D);
 * if (displayObject) {
 *     displayObject.initialize(renderer);
 * }
 * ```
 */

import type {
    DisplayFeatureType,
    DisplayObjectFactory,
    IDisplayFeature,
    IDisplayObject,
    ViewMode,
} from './types';
import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';

export class DisplayObjectRegistry extends PubSubService {
    static readonly SERVICE_NAME = 'DisplayObjectRegistry';

    static readonly REGISTRATION: ServiceRegistration = {
        name: DisplayObjectRegistry.SERVICE_NAME,
        factory: (context: ServiceContext) => new DisplayObjectRegistry(context),
    };

    // No events emitted by this service
    readonly EVENTS = {} as const;

    /** Map key: "featureType_viewMode" → factory function */
    private factories = new Map<string, DisplayObjectFactory>();

    constructor(_context: ServiceContext) {
        super();
    }

    /**
     * Create a registry key from feature type and view mode.
     */
    private key(featureType: DisplayFeatureType, viewMode: ViewMode): string {
        return `${featureType}_${viewMode}`;
    }

    /**
     * Register a factory for a specific feature type and view mode.
     *
     * @param featureType - The feature type this factory handles
     * @param viewMode - The view mode this factory handles
     * @param factory - Factory function to create DisplayObjects
     */
    register(featureType: DisplayFeatureType, viewMode: ViewMode, factory: DisplayObjectFactory): void {
        const k = this.key(featureType, viewMode);
        if (this.factories.has(k)) {
            console.warn(
                `DisplayObject factory for ${featureType} in ${viewMode} is already registered. Overwriting.`
            );
        }
        this.factories.set(k, factory);
    }

    /**
     * Create a DisplayObject for a feature in a given view mode.
     *
     * @param feature - The feature to create a DisplayObject for
     * @param viewMode - The view mode to render in
     * @returns DisplayObject instance, or null if no factory is registered
     */
    create(feature: IDisplayFeature, viewMode: ViewMode): IDisplayObject | null {
        const factory = this.factories.get(this.key(feature.type, viewMode));
        if (!factory) {
            console.warn(
                `No DisplayObject registered for ${feature.type} in ${viewMode}. ` +
                    `Feature "${feature.id}" will not be rendered.`
            );
            return null;
        }
        return factory(feature, viewMode);
    }

    /**
     * Check if a factory exists for a feature type and view mode.
     */
    hasFactory(featureType: DisplayFeatureType, viewMode: ViewMode): boolean {
        return this.factories.has(this.key(featureType, viewMode));
    }

    /**
     * Get all registered (featureType, viewMode) combinations.
     * Useful for debugging and testing.
     */
    getRegisteredCombinations(): Array<{ featureType: string; viewMode: string }> {
        const combinations: Array<{ featureType: string; viewMode: string }> = [];
        for (const key of this.factories.keys()) {
            const [featureType, viewMode] = key.split('_');
            combinations.push({ featureType, viewMode });
        }
        return combinations;
    }

    /**
     * Get the number of registered factories.
     */
    getFactoryCount(): number {
        return this.factories.size;
    }

    /**
     * Reset clears all subscriptions but keeps factory registrations.
     * Factories are registered at app initialization and should persist.
     */
    override reset(): void {
        console.log(`${DisplayObjectRegistry.SERVICE_NAME} reset called`);
        // Note: We intentionally do NOT clear factories here.
        // Factories are registered once at app initialization.
        // Only subscriptions are cleared via super.reset().
        super.reset();
    }
}
